(function () {
  if (window.customElements.get("cart-upsell")) return;

  // Hidden cart attribute carrying the visitor's FX rate for the server-side
  // tier/gap computation in snippets/cart-upsell.liquid (Liquid cannot read
  // Shopify.currency.rate). Set via cart/update.js, which also asks for the
  // cart sections back in the same response.
  const FX_ATTR = "_upsell_fx_rate";

  function parseAttr(element, name, fallback) {
    try {
      const parsed = JSON.parse(element.getAttribute(name));
      return parsed == null ? fallback : parsed;
    } catch (error) {
      return fallback;
    }
  }

  /**
   * Which values are still reachable on each axis, given the selections already
   * made on the axes before it. Mirrors CartItems._possibleValues in
   * assets/global.js so the upsell switcher behaves exactly like the cart line
   * switcher. Variants whose `available` is false (sold out or pre-order, as
   * baked in by snippets/upsell-candidate-card.liquid) grey out.
   */
  function possibleValues(variants, selected) {
    const available = variants.filter((variant) => variant.available);
    const axes = [[], [], []];

    for (const variant of available) {
      if (variant.options[0] != null && !axes[0].includes(variant.options[0])) axes[0].push(variant.options[0]);
    }
    if (selected[0] != null) {
      for (const variant of available) {
        if (variant.options[0] !== selected[0]) continue;
        if (variant.options[1] != null && !axes[1].includes(variant.options[1])) axes[1].push(variant.options[1]);
      }
    }
    if (selected[0] != null && selected[1] != null) {
      for (const variant of available) {
        if (variant.options[0] !== selected[0] || variant.options[1] !== selected[1]) continue;
        if (variant.options[2] != null && !axes[2].includes(variant.options[2])) axes[2].push(variant.options[2]);
      }
    }
    return axes;
  }

  /**
   * A variant is only resolvable once every axis the product actually has is
   * chosen and the combination narrows to exactly one variant. Mirrors
   * CartItems._resolveVariant in assets/global.js.
   */
  function resolveVariant(variants, options, selected) {
    const axisCount = options.length;
    for (let axis = 0; axis < axisCount; axis += 1) {
      if (selected[axis] == null) return null;
    }
    const matches = variants.filter((variant) => {
      for (let axis = 0; axis < axisCount; axis += 1) {
        if (variant.options[axis] !== selected[axis]) return false;
      }
      return true;
    });
    return matches.length === 1 ? matches[0] : null;
  }

  /**
   * Companion for the server-rendered cart upsell. All selection (tier, gap,
   * pool ranking, the final two picks) happens in Liquid — this element only:
   *   - syncs the FX rate into the hidden cart attribute whenever it drifts,
   *     so every later section render (piggybacked on cart mutations by
   *     _erj_.cartSections) can compute the gap server-side;
     *   - drives the variant switcher on the offer cards;
     *   - wires the + add button (optimistic hide until the real re-render),
     *     tagging the line with the hidden property _from=cart-upsell; the
     *     click itself is tracked in assets/custom_track.js
     *     (cart_upsell_add_to_cart).
   */
  class CartUpsell extends HTMLElement {
    constructor() {
      super();
      this._fxSyncInFlight = false;
      this._onCartUpdate = this._onCartUpdate.bind(this);
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      // Click delegation on the host: section swaps replace innerHTML, so
      // per-card listeners would be lost.
      this.addEventListener("click", this._onClick);
      window.addEventListener("enroute:cart-updated", this._onCartUpdate);
      // Every page load: verify the stored FX rate against the current one
      // (window.cart.attributes ships server-side, so the check itself costs
      // no request) and re-store it when they differ.
      this._checkFxRateOnLoad();
    }

    disconnectedCallback() {
      window.removeEventListener("enroute:cart-updated", this._onCartUpdate);
      this.removeEventListener("click", this._onClick);
    }

    _onCartUpdate(event) {
      if (!event || !event.detail || !event.detail.cart) return;
      if (event.detail.optimistic) {
        // A failed optimistic add (rollback) must bring hidden cards back;
        // the next authoritative render replaces the module anyway.
        if (event.detail.rollback) this._unhideAll();
        return;
      }
      // Safety net beyond the load-time check (covers a currency change that
      // somehow skips a reload).
      this._syncFxRateFrom(event.detail.cart.attributes);
    }

    async _checkFxRateOnLoad() {
      let attributes =
        (window.cart && window.cart.attributes) ||
        (window.__enroutePromoBarState && window.__enroutePromoBarState.cart && window.__enroutePromoBarState.cart.attributes) ||
        null;
      if (!attributes) {
        // window.cart missing/unparsed — fall back to one cart.js round trip.
        try {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
          const response = await fetch(root + "cart.js");
          if (response.ok) attributes = (await response.json()).attributes || {};
        } catch (error) {
          return;
        }
      }
      this._syncFxRateFrom(attributes);
    }

    /**
     * Store the visitor's exchange rate on the cart when missing or stale.
     * The same cart/update.js call asks for the cart sections back, so the
     * very first sync also delivers the filled-in module (before the rate is
     * stored the section renders empty by design — "You might also like"
     * loads late).
     */
    _syncFxRateFrom(attributes) {
      const sections = window._erj_ && window._erj_.cartSections;
      if (!sections || typeof sections.mutateCart !== "function") return;
      if (this._fxSyncInFlight) return;
      const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
      const stored = (attributes || {})[FX_ATTR];
      if (String(stored || "") === String(rate)) return;

      this._fxSyncInFlight = true;
      sections
        .mutateCart("update.js", { attributes: { [FX_ATTR]: String(rate) } })
        .then(() => {
          this._fxSyncInFlight = false;
        })
        .catch(() => {
          this._fxSyncInFlight = false;
        });
    }

    _unhideAll() {
      for (const card of this.querySelectorAll("[data-upsell-pool]")) {
        card.hidden = false;
      }
    }

    _onClick(event) {
      const optionButton = event.target.closest("[data-variant-option]");
      if (optionButton && this.contains(optionButton)) {
        event.preventDefault();
        event.stopPropagation();
        this._onVariantOptionClick(optionButton);
        return;
      }

      const switcherToggle = event.target.closest("[data-variant-switcher-toggle]");
      if (switcherToggle && this.contains(switcherToggle)) {
        event.preventDefault();
        event.stopPropagation();
        this._toggleSwitcher(switcherToggle.closest("[data-variant-switcher]"));
        return;
      }

      const btn = event.target.closest("[data-cart-upsell-add]");
      if (btn && this.contains(btn)) {
        event.preventDefault();
        this._onAddClick(btn);
        return;
      }

      // Clicking anywhere else closes any open switcher popup, same as the
      // cart line items (assets/global.js CartItems._onClick).
      this._closeAllSwitchers();
    }

    /* ------------------------------------------------------- variant switcher */

    _closeAllSwitchers(exceptPopup) {
      for (const popup of this.querySelectorAll("[data-variant-switcher-popup]")) {
        if (exceptPopup && popup === exceptPopup) continue;
        popup.hidden = true;
        popup.closest("[data-variant-switcher]")?.querySelector("[data-variant-switcher-toggle]")?.setAttribute("aria-expanded", "false");
      }
    }

    _toggleSwitcher(switcher) {
      if (!switcher) return;
      const popup = switcher.querySelector("[data-variant-switcher-popup]");
      if (!popup) return;
      const willOpen = popup.hidden;
      this._closeAllSwitchers(popup);
      popup.hidden = !willOpen;
      switcher.querySelector("[data-variant-switcher-toggle]")?.setAttribute("aria-expanded", String(willOpen));
      if (willOpen) {
        this._paintSwitcherState(switcher);
        // Recheck edges after painting options and reposition to avoid viewport/container clipping.
        popup.reposition();
      }
    }

    _paintSwitcherState(switcher) {
      const variants = parseAttr(switcher, "data-variants", []);
      const selected = parseAttr(switcher, "data-selected", [null, null, null]);
      const reachable = possibleValues(variants, selected);

      for (const optionEl of switcher.querySelectorAll("[data-variant-option]")) {
        const axis = (Number(optionEl.dataset.optionPosition) || 1) - 1;
        const value = optionEl.dataset.optionValue;
        const isAvailable = reachable[axis] ? reachable[axis].includes(value) : false;
        const isSelected = selected[axis] === value;

        // data-variant-option is on the whole row (check zone is clickable too);
        // the name/disabled look lives on the inner name node.
        const nameEl = optionEl.querySelector(
          ".hvg-cart-variant-switcher-popup-optionblock-item-name, .hvg-cart-variant-switcher-popup-optionblock-item-name-disabled"
        );
        if (nameEl) {
          nameEl.classList.toggle("hvg-cart-variant-switcher-popup-optionblock-item-name", isAvailable);
          nameEl.classList.toggle("hvg-cart-variant-switcher-popup-optionblock-item-name-disabled", !isAvailable);
        }
        optionEl.toggleAttribute("data-disabled", !isAvailable);
        optionEl.classList.toggle("is-selected", isSelected);

        const check = optionEl.querySelector("[data-variant-option-check]");
        if (check) check.hidden = !isSelected;
      }
    }

    _onVariantOptionClick(optionEl) {
      const switcher = optionEl.closest("[data-variant-switcher]");
      if (!switcher) return;

      const variants = parseAttr(switcher, "data-variants", []);
      const selected = parseAttr(switcher, "data-selected", [null, null, null]);
      const axis = (Number(optionEl.dataset.optionPosition) || 1) - 1;
      const value = optionEl.dataset.optionValue;

      // Disabled values are inert, same as the cart line switcher.
      const reachable = possibleValues(variants, selected);
      if (!reachable[axis] || !reachable[axis].includes(value)) return;

      selected[axis] = value;
      switcher.setAttribute("data-selected", JSON.stringify(selected));
      this._paintSwitcherState(switcher);

      // Nothing to apply until every axis is chosen and one variant matches.
      const options = parseAttr(switcher, "data-options", []);
      const targetVariant = resolveVariant(variants, options, selected);
      if (!targetVariant) return;

      const popup = switcher.querySelector("[data-variant-switcher-popup]");
      if (popup) popup.hidden = true;

      const card = switcher.closest("[data-upsell-pool]");
      if (!card || String(targetVariant.id) === String(card.dataset.variantId)) return;
      this._applyVariantToCard(card, switcher, targetVariant);
    }

    /**
     * Re-point an offer card at another variant of the same product: the
     * dataset (so the + button adds the new variant), the per-variant price
     * blocks and the thumbnail background (each variants-JSON entry carries
     * its image URL, variant's own with the product image as fallback).
     * No cart mutation — unlike the line-item switcher in assets/global.js
     * there is no line to remove and re-add.
     */
    _applyVariantToCard(card, switcher, targetVariant) {
      card.dataset.variantId = String(targetVariant.id);
      card.dataset.price = String(targetVariant.price);
      card.dataset.sku = targetVariant.sku || "";
      card.dataset.variantText = (targetVariant.options || [])
        .filter(function (value) { return value != null && value !== ""; })
        .join(" / ");

      if (targetVariant.image) {
        card.dataset.productImage = targetVariant.image;
        const thumb = card.querySelector(".thumb");
        if (thumb) thumb.style.backgroundImage = 'url("' + targetVariant.image + '")';
      }

      for (const priceBlock of card.querySelectorAll("[data-variant-price-for]")) {
        priceBlock.hidden = String(priceBlock.dataset.variantPriceFor) !== String(targetVariant.id);
      }

      const infoItems = switcher.querySelector(".hvg-cart-variant-switcher-info-items");
      if (infoItems) {
        infoItems.innerHTML = "";
        for (const value of targetVariant.options || []) {
          if (value == null || value === "") continue;
          const span = document.createElement("span");
          span.textContent = value;
          infoItems.appendChild(span);
        }
      }
    }

    /* ------------------------------------------------------------- add to cart */

    _onAddClick(btn) {
      if (typeof window.enrouteCart?.addToCart !== "function") return;
      const item = btn.closest("[data-upsell-pool]");
      if (!item) return;
      const variantId = item.dataset.variantId;
      if (!variantId) return;
      // Hide immediately; the authoritative cart update re-renders the module
      // server-side with the product filtered out of every pool.
      item.hidden = true;
      window.enrouteCart?.addToCart?.({
        id: Number(variantId),
        quantity: 1,
        // Hidden line-item property (underscore keys are kept out of the
        // visible property list in cart-items.liquid) marking the line's
        // origin for downstream reporting.
        properties: { _from: "cart-upsell" },
        productId: Number(item.dataset.productId),
        handle: item.dataset.handle || "",
        title: item.dataset.productTitle || "",
        image: item.dataset.productImage || "",
        variantText: item.dataset.variantText || "",
        url: item.dataset.productUrl || "",
      });
    }
  }

  window.customElements.define("cart-upsell", CartUpsell);
})();
